import { months } from "../helper";
import { formatNumber } from "../number";

// Distribuição por hora do dia, compartilhada pelas telas de Auditoria.
// Segue a forma de `salesPerHourKPI` (dashboard/sales/helper): index "hour",
// eixo 0-23 completo e uma série por mês.

export const HORAS_DO_DIA = Array.from({ length: 24 }, (_, hora) => hora);

// É contagem de ocorrências, não dinheiro: o KpiAreaChart formata como R$ por
// padrão e o valueFormatter daqui sobrescreve isso (o {...props} vem depois).
export const formatarContagem = (valor) => formatNumber(valor || 0, { maximumFractionDigits: 0 });

// `hora` chega como número (date_part vira float8 no driver do pg) ou string,
// dependendo de como o payload trafegou. Fora de 0-23, ausente ou não numérico
// vira null — e registro sem hora simplesmente não entra na contagem.
export function normalizarHora(valor) {
    if (valor === null || valor === undefined || valor === '') {
        return null;
    }

    const numero = Number(valor);

    if (!Number.isFinite(numero)) {
        return null;
    }

    const hora = Math.trunc(numero);

    return hora >= 0 && hora <= 23 ? hora : null;
}

function contarPorHora(registros) {
    const porHora = new Map();
    let comHora = 0;

    (Array.isArray(registros) ? registros : []).forEach((registro) => {
        const hora = normalizarHora(registro?.hora);

        if (hora === null) {
            return;
        }

        comHora += 1;
        porHora.set(hora, (porHora.get(hora) || 0) + 1);
    });

    return { porHora, comHora };
}

/**
 * Agrega séries de registros por hora do dia.
 *
 * @param {Array<{nome: string, registros: Array}>} series uma entrada por mês.
 * @returns {{temHora: boolean, data: Array}} `temHora` é false quando nenhum registro
 * trouxe hora utilizável — é o caso da view ainda sem a coluna, em que o gráfico
 * precisa sumir em vez de desenhar 24 zeros, que seriam lidos como "não houve nada".
 */
export function distribuicaoPorHora(series) {
    const listas = (Array.isArray(series) ? series : []).map((serie) => ({
        nome: serie?.nome,
        contagem: contarPorHora(serie?.registros),
    }));

    const temHora = listas.some((serie) => serie.contagem.comHora > 0);

    const data = HORAS_DO_DIA.map((hour) => {
        const linha = { hour };

        listas.forEach((serie) => {
            linha[serie.nome] = serie.contagem.porHora.get(hour) || 0;
        });

        return linha;
    });

    return { temHora, data };
}

/**
 * Monta o card de "por hora" já no formato que o KpiAreaChart espera.
 * Mês atual e anterior viram séries separadas — os registros dos dois meses chegam
 * na mesma lista e não podem ser somados na mesma barra.
 */
export function construirGraficoPorHora({ caption, currentMonth, registrosMesAtual, registrosMesAnterior }) {
    const vazio = {
        caption,
        data: [],
        index: "hour",
        categories: [],
        colors: ["indigo", "cyan"],
        valueFormatter: formatarContagem,
    };

    const indiceMesAtual = Number(currentMonth) - 1;

    if (!Number.isInteger(indiceMesAtual) || indiceMesAtual < 0 || indiceMesAtual > 11) {
        return vazio;
    }

    const indiceMesAnterior = (indiceMesAtual - 1 + 12) % 12;

    const descricaoMesAtual = `${months[indiceMesAtual]}`;
    const descricaoMesAnterior = `${months[indiceMesAnterior]}`;

    const { temHora, data } = distribuicaoPorHora([
        { nome: descricaoMesAnterior, registros: registrosMesAnterior },
        { nome: descricaoMesAtual, registros: registrosMesAtual },
    ]);

    if (!temHora) {
        return vazio;
    }

    return {
        ...vazio,
        data,
        categories: [descricaoMesAnterior, descricaoMesAtual],
    };
}
